import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import CommonInput from '../../component/form/common-input.component';
import DialogComponent from '../../component/dialog/dialog.component';
import i18n from '../../i18n';

/** GYB 设备详情只读字段（与 RBP2 GYB 表单展示项一致，标签复用餐厅/GYB 已有 i18n） */
const GYB_READONLY_FIELDS: { key: string; labelKey: string }[] = [
    { key: 'gyb_res_vat_number', labelKey: 'gyb_page_text_VAT_registration_number' },
    { key: 'gyb_res_address', labelKey: 'restaurant_info_page_text_street' },
    { key: 'gyb_res_tax_code', labelKey: 'gyb_page_text_Tax_number' },
    { key: 'gyb_res_zip_code', labelKey: 'restaurant_info_page_text_postcode' },
    { key: 'gyb_res_city', labelKey: 'restaurant_info_page_text_city' },
    { key: 'gyb_res_description', labelKey: 'gyb_page_text_describe' },
    { key: 'gyb_res_province', labelKey: 'address_component_text_province' },
    { key: 'gyb_res_country', labelKey: 'restaurant_info_page_text_country' },
    { key: 'location_id', labelKey: 'gyb_page_text_registered_id' },
    { key: 'operator', labelKey: 'gyb_page_text_operator' },
    { key: 'device_code', labelKey: 'gyb_page_text_the_device_number' },
    { key: 'model', labelKey: 'gyb_page_text_model' },
    { key: 'license_code', labelKey: 'gyb_page_text_GYB_key' },
    { key: 'valore_significato', labelKey: 'dis_setting_page_gyb_valore_significato' },
    { key: 'gyb_dealer_username', labelKey: 'gyb_page_text_name' },
    { key: 'gyb_dealer_name', labelKey: 'gyb_page_text_the_key_name' },
];

export type DisGybDetailRecord = Record<string, string | number | undefined>;

type DisGybDetailDrawerProps = {
    /** 是否打开 */
    open: boolean;
    /** 关闭 */
    onClose: () => void;
    /** 设备详情 */
    detail: DisGybDetailRecord;
};

/** 与 `call-service.page` 中「更多设置」弹窗同一套纸面/边框样式 */
const DIALOG_SX = {
    '& .MuiPaper-root.dis-gyb-detail-dialog__paper': {
        background: 'var(--fill-neutral-100, #fff)',
        backgroundImage: 'none',
        border: '1px solid rgba(31, 41, 64, 0.12)',
    },
} as const;

/**
 * GYB 设备详情弹窗：字段只读，与 RBP2 `CommomSettingDetail` GYB 分支一致；版式对齐呼叫服务编辑弹窗。
 */
const DisGybDetailDrawer: React.FC<DisGybDetailDrawerProps> = ({ open, onClose, detail }) => {
    /** 展示用数据（含写死的 valore_significato 兜底） */
    const [row, setRow] = useState<DisGybDetailRecord>({});

    useEffect(() => {
        if (!open) {
            return;
        }
        setRow({
            ...detail,
            valore_significato: detail.valore_significato ?? '2C Solution',
        });
    }, [open, detail]);

    /** 与 MUI `Dialog` / 公共头 `onClose` 签名对齐；关窗仅顶栏关闭，无底栏「返回」 */
    const handleClose = () => onClose();

    return (
        <DialogComponent
            open={open}
            fullWidth={false}
            maxWidth={false}
            scroll="paper"
            className="dis-gyb-detail-dialog"
            onClose={handleClose}
            sx={DIALOG_SX}
            PaperProps={{
                className: 'dis-gyb-detail-dialog__paper',
                sx: {
                    width: 768,
                    maxWidth: 'calc(100vw - 48px)',
                    borderRadius: '16px',
                    boxSizing: 'border-box',
                },
            }}
            defaultHeaderProps={{
                titleContent: (
                    <Typography
                        component="div"
                        sx={{
                            fontWeight: 700,
                            fontSize: 20,
                            lineHeight: '24px',
                            color: 'primary.main',
                        }}
                    >
                        {i18n.t('google_text_detail')}
                    </Typography>
                ),
                onClose: handleClose,
            }}
            dialogContentStyle={{
                paddingTop: 24,
                paddingLeft: 16,
                paddingRight: 16,
                paddingBottom: 0,
                maxHeight: 'min(72vh, 800px)',
                overflow: 'auto',
            }}
        >
            <Box sx={{ pb: 3, width: '100%' }}>
                <Grid container spacing={2}>
                    {GYB_READONLY_FIELDS.map((f) => (
                        <Grid item xs={12} sm={6} key={f.key}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {i18n.t(f.labelKey)}
                            </Typography>
                            <CommonInput
                                inputType="INPUT"
                                size="small"
                                fullWidth
                                disabled
                                value={String(row[f.key] ?? '')}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </DialogComponent>
    );
};

export default DisGybDetailDrawer;
